import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { IconAlertTriangle, IconCheck, IconX } from "@tabler/icons-react";

export type ToastKind = "success" | "error";

export function errorMessage(error: unknown): string {
  const anyErr = error as { response?: { data?: { message?: string } } };
  return anyErr?.response?.data?.message ?? "Something went wrong. Please try again.";
}

export interface ToastState {
  kind: ToastKind;
  message: string;
  // Bumped per toast so re-showing the same message restarts the timer.
  id: number;
}

const KIND = {
  success: { accent: "#059669", tint: "#ecfdf5", Icon: IconCheck, ms: 4000 },
  error: { accent: "#dc2626", tint: "#fef2f2", Icon: IconAlertTriangle, ms: 7000 },
} as const;

export function useToast() {
  const [toast, setToast] = useState<ToastState | null>(null);

  const showToast = useCallback(
    (kind: ToastKind, message: string) => setToast({ kind, message, id: Date.now() }),
    []
  );
  const dismissToast = useCallback(() => setToast(null), []);

  return { toast, showToast, dismissToast };
}

// Portalled to document.body, not rendered in place: the bundles mount into a
// container on a host page we don't control, and `position: fixed` resolves
// against the nearest transformed/contained ancestor rather than the viewport.
// Styling is entirely inline (no Mantine classes, no CSS variables), so it
// renders the same outside the bundle's scoped root as inside it.
export function Toast({ toast, onDismiss }: { toast: ToastState | null; onDismiss: () => void }) {
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (!toast) return;
    const raf = requestAnimationFrame(() => setShown(true));
    const timer = setTimeout(onDismiss, KIND[toast.kind].ms);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timer);
      setShown(false);
    };
  }, [toast, onDismiss]);

  if (!toast) return null;

  const { accent, tint, Icon } = KIND[toast.kind];

  return createPortal(
    <div
      role="status"
      aria-live="polite"
      style={{
        position: "fixed",
        bottom: 24,
        left: "50%",
        transform: `translateX(-50%) translateY(${shown ? 0 : 8}px)`,
        opacity: shown ? 1 : 0,
        transition: "opacity 160ms ease-out, transform 160ms ease-out",
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        maxWidth: "min(92vw, 440px)",
        padding: "12px 14px",
        borderRadius: 10,
        background: "#ffffff",
        border: `1px solid ${accent}33`,
        borderLeft: `4px solid ${accent}`,
        boxShadow: "0 10px 25px rgba(15, 23, 42, 0.15)",
        zIndex: 2000,
      }}
    >
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          width: 20,
          height: 20,
          borderRadius: 999,
          background: tint,
          color: accent,
        }}
      >
        <Icon size={13} stroke={3} />
      </span>

      <span style={{ fontSize: 13, lineHeight: 1.45, color: "#0f172a", overflowWrap: "anywhere" }}>
        {toast.message}
      </span>

      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        style={{
          display: "inline-flex",
          flexShrink: 0,
          marginLeft: 4,
          padding: 2,
          border: "none",
          background: "transparent",
          color: "#94a3b8",
          cursor: "pointer",
        }}
      >
        <IconX size={14} />
      </button>
    </div>,
    document.body
  );
}
